// AI insight + recommendation panels (prototype aiInsight / aiRecs).

import React from 'react';
import { cn } from '@/lib/utils';
import { analyseDrivers } from './engine/insights';
import type { InsightOpts } from './engine/insights';
import type { Worker } from './engine/data';

export const AiInsight: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (children === null || children === undefined || children === false || children === '') return null;
  return (
    <div className='flex items-start gap-3 rounded-md border border-l-2 border-border border-l-info bg-card px-4 py-3.5'>
      <div className='min-w-0'>
        <div className='mb-1 text-[11px] font-medium text-info'>Insight</div>
        <div className='text-[13px] leading-relaxed text-foreground'>{children}</div>
      </div>
    </div>
  );
};

// Rank 1 = red, 2 = amber, 3+ = blue (prototype rec-priority-1/2/3).
const PRIORITY = [
  { border: 'border-l-destructive', badge: 'bg-destructive/10 text-destructive' },
  { border: 'border-l-warning', badge: 'bg-warning/10 text-warning' },
  { border: 'border-l-info', badge: 'bg-info/10 text-info' },
] as const;

export const AiRecs: React.FC<{ workers: Worker[]; opts?: InsightOpts; scopeLabel?: string }> = ({
  workers,
  opts,
  scopeLabel,
}) => {
  const steps = React.useMemo(() => analyseDrivers(workers, opts), [workers, opts]);
  if (!steps.length) return null;
  const scopeName = scopeLabel || opts?.scopeName || 'this group';
  return (
    <div className='overflow-hidden rounded-md border border-border bg-card'>
      <div className='flex items-center gap-2 border-b border-border px-4 py-2.5'>
        <div className='text-[13px] font-semibold'>Recommended next steps</div>
        <div className='ml-auto text-xs text-muted-foreground'>{scopeName}</div>
      </div>
      <div className='divide-y divide-border'>
        {steps.map((s, i) => {
          const p = PRIORITY[Math.min(i, PRIORITY.length - 1)];
          return (
            <div key={s.action} className={cn('flex items-start gap-3 border-l-2 px-4 py-3', p.border)}>
              <div
                className={cn('flex w-16 shrink-0 flex-col items-center gap-1 rounded-[4px] px-1.5 py-1.5', p.badge)}
              >
                <s.icon className='h-4 w-4' />
                <div className='text-[9px] font-semibold uppercase tracking-wider'>{s.label}</div>
              </div>
              <div className='min-w-0'>
                <div className='text-[13px] font-semibold'>{s.action}</div>
                <div className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>{s.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
